import React, { useState, useEffect, useRef, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css"; // Import global theme styles here
import "./App.css";

// ✅ Import the UI components
import DiaryFormat from "./DiaryFormat";
import StandardFormat from "./StandardFormat";
import WorkFormat from "./WorkFormat";
import CalendarFormat from "./CalendarFormat";
import { useAuth } from "./AuthContext";
import Login from "./Login";
import PermissionDenied from "./PermissionDenied"; 

const App = () => {
  const { user, allowed, authLoading, signOut } = useAuth();
  const [panchangData, setPanchangData] = useState([]);
  const [viewMode, setViewMode] = useState("standard"); // Default: Standard Format
  const [manifest, setManifest] = useState([]); // Array of { year, language, file }
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    try {
      return localStorage.getItem("panchang-language") || "English";
    } catch {
      return "English";
    }
  });
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialSelectionSetRef = useRef(false);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("panchang-theme") || "dark";
    } catch {
      return "dark";
    }
  });
  const [showMidnight, setShowMidnight] = useState(() => {
    try {
      const v = localStorage.getItem("panchang-show-midnight");
      return v === null || v === "true";
    } catch {
      return true;
    }
  });

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem("panchang-theme", next);
    } catch (_) {}
  };

  const toggleShowMidnight = () => {
    setShowMidnight((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("panchang-show-midnight", String(next));
      } catch (_) {}
      return next;
    });
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Derive available years and languages from the manifest
  const availableYears = useMemo(() => {
    const years = [...new Set(manifest.map((e) => e.year))].sort((a, b) => b - a);
    return years;
  }, [manifest]);

  const languagesForYear = useMemo(() => {
    if (selectedYear == null) return [];
    return manifest
      .filter((e) => e.year === selectedYear)
      .map((e) => e.language);
  }, [manifest, selectedYear]);

  // Find the file path for the current year + language selection
  const selectedFile = useMemo(() => {
    if (selectedYear == null || !selectedLanguage) return null;
    const entry = manifest.find(
      (e) => e.year === selectedYear && e.language === selectedLanguage
    );
    return entry?.file || null;
  }, [manifest, selectedYear, selectedLanguage]);

  // Load manifest on mount
  useEffect(() => {
    setLoading(true);
    fetch("/panchang_manifest.json")
      .then(async (res) => {
        if (!res.ok) throw new Error("Manifest not found");
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data) || data.length === 0) {
          setManifest([]);
          return;
        }
        setManifest(data);
        // Set initial year + language
        if (!initialSelectionSetRef.current) {
          initialSelectionSetRef.current = true;
          const years = [...new Set(data.map((e) => e.year))].sort((a, b) => b - a);
          const firstYear = years[0];
          setSelectedYear(firstYear);
          // Prefer previously-saved language, else first available for that year
          const langsForFirst = data.filter((e) => e.year === firstYear).map((e) => e.language);
          const savedLang = (() => { try { return localStorage.getItem("panchang-language"); } catch { return null; } })();
          if (savedLang && langsForFirst.includes(savedLang)) {
            setSelectedLanguage(savedLang);
          } else {
            setSelectedLanguage(langsForFirst[0] || "English");
          }
        }
      })
      .catch(() => setManifest([]))
      .finally(() => setLoading(false));
  }, []);

  // When year changes, ensure the selected language is valid for that year
  useEffect(() => {
    if (selectedYear == null || languagesForYear.length === 0) return;
    if (!languagesForYear.includes(selectedLanguage)) {
      setSelectedLanguage(languagesForYear[0]);
    }
  }, [selectedYear, languagesForYear, selectedLanguage]);

  // Persist language selection
  useEffect(() => {
    try {
      localStorage.setItem("panchang-language", selectedLanguage);
    } catch (_) {}
  }, [selectedLanguage]);

  // Load panchang data when the selected file changes
  useEffect(() => {
    if (!selectedFile) return;
    setLoadError(null);
    setLoading(true);
    fetch(`/${selectedFile}`)
      .then(async (res) => {
        const contentType = (res.headers.get("content-type") || "").toLowerCase();
        const text = await res.text();
        if (!res.ok) {
          throw new Error(`No data for ${selectedYear} (${selectedLanguage})`);
        }
        if (!contentType.includes("application/json")) {
          throw new Error(`No data available for ${selectedYear} (${selectedLanguage}).`);
        }
        try {
          return JSON.parse(text);
        } catch {
          throw new Error(`No data available for ${selectedYear} (${selectedLanguage}).`);
        }
      })
      .then((data) => {
        setPanchangData(data);
        setLoadError(null);
      })
      .catch((err) => {
        setPanchangData([]);
        setLoadError(err.message || "Error loading panchang data.");
      })
      .finally(() => setLoading(false));
  }, [selectedFile, selectedYear, selectedLanguage]);

  if (authLoading) {
    return (
      <div className="app-wrapper" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  if (!allowed) {
    return <PermissionDenied />;
  }

  return (
    <div className={`app-wrapper theme-${theme}`}>
    <div className="container container--mobile mt-4">
      {/* UI Selection Dropdown */}
      <div className="app-toolbar mb-3 text-center">
        <div className="app-toolbar-row d-flex flex-wrap align-items-center justify-content-center gap-2">
          <span className="text-muted small me-2">Signed in as {user.email}</span>
          <span className="text-muted small me-2 d-none d-sm-inline" aria-hidden="true">·</span>
          <button
            type="button"
            className="btn theme-toggle-btn"
            onClick={signOut}
            title="Sign out"
            aria-label="Sign out"
          >
            Sign out
          </button>
          <button
            type="button"
            className="btn theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={theme === "dark" ? "Light mode" : "Dark mode"}
          >
            {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
          </button>
          <label className="d-flex align-items-center gap-1 ms-2 mb-0 cursor-pointer" title="Show or hide 00:00 for midnight times in all formats">
            <input
              type="checkbox"
              className="form-check-input"
              checked={showMidnight}
              onChange={toggleShowMidnight}
              aria-label="Show 00:00"
            />
            <span className="small fw-bold">Show 00:00</span>
          </label>
        </div>
        <div className="app-toolbar-row d-flex flex-wrap align-items-center justify-content-center gap-2 mt-2">
          {availableYears.length > 0 && (
            <>
              <label htmlFor="yearSelect" className="me-2 fw-bold">Year:</label>
              <select
                id="yearSelect"
                className="form-select form-select--mobile d-inline-block w-auto me-3"
                value={selectedYear ?? ""}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                disabled={loading}
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </>
          )}
          {languagesForYear.length > 1 && (
            <>
              <label htmlFor="languageSelect" className="me-2 fw-bold">Language:</label>
              <select
                id="languageSelect"
                className="form-select form-select--mobile d-inline-block w-auto me-3"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                disabled={loading}
              >
                {languagesForYear.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </>
          )}
          <label htmlFor="viewMode" className="me-2 fw-bold">Choose View:</label>
          <select
            id="viewMode"
            className="form-select form-select--mobile d-inline-block w-auto"
            onChange={(e) => setViewMode(e.target.value)}
            value={viewMode}
          >
            <option value="diary">Diary Format</option>
            <option value="standard">Standard Format</option>
            <option value="work">Work Mode</option>
            <option value="calendar">Calendar View</option>
          </select>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-warning text-center" role="alert">
          {loadError}
        </div>
      )}
      {loading && manifest.length === 0 && (
        <div className="text-center text-muted">Checking for panchang data…</div>
      )}
      {!loading && manifest.length === 0 && (
        <div className="alert alert-info text-center">
          No panchang data found. Add entries to panchang_manifest.json in the public folder.
        </div>
      )}

      {/* Render UI based on selection */}
      {viewMode === "standard" && <StandardFormat panchangData={panchangData} showMidnight={showMidnight} />}
      {viewMode === "diary" && <DiaryFormat panchangData={panchangData} showMidnight={showMidnight} />}
      {viewMode === "work" && <WorkFormat panchangData={panchangData} showMidnight={showMidnight} />}
      {viewMode === "calendar" && <CalendarFormat panchangData={panchangData} showMidnight={showMidnight} />}
    </div>
    </div>
  );
};

export default App;